import Button from "@material-ui/core/Button";
import withStyles from "@material-ui/core/styles/withStyles";
import { FORM_ERROR, FormApi, FormState } from "final-form";
import { inject, observer } from "mobx-react";
import queryString from "query-string";
import React from "react";
import { Form, FormRenderProps } from "react-final-form";
import { RouteComponentProps } from "react-router";
import { OneDataSource } from "../../../dataAccess/DataSource";
import { FieldError } from "../../../model/CommonsStore";
import {
  DynamicForm,
  PresentationComponentProps
} from "../../DynamicForm/DynamicForm";
import arrayMutators from "final-form-arrays";
import { WithStyles } from "@material-ui/styles";
import { LoggerFactory } from "../../../logger";
import { FlatForm } from "../ShowView/FlatForm";
import { TabsForm } from "../ShowView/TabsForm";
import {
  DefaultContentContainer,
  DefaultTitleContainer,
  DefaultViewContainer,
  OneViewProps,
  viewStyles
} from "../OneView";
import { TitleRow } from "../TitleRow";
import { GoBackButton, I18n, SecurityProvider } from "../../../index";
import { disableable } from "../../../helpers/renderUtils";

export interface CreateViewProps<T, ID>
  extends OneViewProps<T, ID>,
    WithStyles<typeof viewStyles>,
    Partial<RouteComponentProps> {
  id?: ID;
  parentDataSource?: OneDataSource<T, ID>;
  initialValues?: {};
  i18n?: I18n;
  submit?: () => Promise<void>;
}

const log = LoggerFactory.create("CreateView");

@inject("router", "i18n")
@observer
class CreateViewComponent<T extends object, ID> extends React.Component<
  CreateViewProps<T, ID>
> {
  public state = {
    isCopyCreate: false,
    initial: {}
  };

  public constructor(props: CreateViewProps<T, ID>) {
    super(props);
  }

  public componentDidMount() {
    if (this.props.id && Object.keys(this.props.id).length !== 0) {
      this.props.dataSource.clear();
      this.props.dataSource.findOne(this.props.id).then(() => {
        this.props.dataSource.clearPkFields();
        this.setState({
          initial: { ...this.props.dataSource.one, ...this.props.initialValues }
        });
      });
      this.setState({ isCopyCreate: true });
    }
  }

  public render() {
    const { classes, title, titleRow, titleVariant, prefix } = this.props;
    const finalTitle = title || "Create";
    let query;
    if (this.props.location) {
      query = queryString.parse(this.props.location.search, {
        arrayFormat: "bracket"
      });
    }
    if (this.props.initialValues) {
      query = { ...query, ...this.props.initialValues };
    }

    const ViewContainer = this.props.viewContainer || DefaultViewContainer;
    const TitleContainer = this.props.titleContainer || DefaultTitleContainer;
    const ContentContainer =
      this.props.contentContainer || DefaultContentContainer;

    return (
      <SecurityProvider security={this.props.dataSource.schema.security}>
        <ViewContainer>
          {titleRow !== false ? (
            <TitleContainer>
              <TitleRow
                classes={classes}
                titleVariant={titleVariant}
                prefix={disableable(
                  prefix,
                  <GoBackButton goBack={this.goBack} />
                )}
                title={finalTitle}
              />
            </TitleContainer>
          ) : null}
          <ContentContainer>
            <Form
              onSubmit={this.submit}
              render={this.dynamicForm}
              initialValues={
                this.state.isCopyCreate ? this.state.initial : query
              }
              mutators={{ ...arrayMutators }}
            />
          </ContentContainer>
        </ViewContainer>
      </SecurityProvider>
    );
  }

  public submit = async (
    values: object,
    form: FormApi,
    callback?: (errors?: object) => void
  ) => {
    try {
      const response = await ((this.props.submit && this.props.submit()) ||
        this.props.dataSource.save());
      if (callback) {
        callback();
      }
      this.goBack(response);
    } catch (e) {
      const errorObj = {};
      if (e.response && e.response.data) {
        if (e.response.data.errors) {
          e.response.data.errors.forEach((error: FieldError) => {
            errorObj[error.fieldName] = error.message;
          });
        } else if (e.response.data.message) {
          errorObj[FORM_ERROR] = e.response.data.message;
        } else {
          log.error("failed to submit form", e);
        }
      }
      callback!(errorObj);
    }
  };

  public goBack = (response?: any) => {
    if (this.state.isCopyCreate && !this.props.goBack) {
      this.props.router!.goUpTwice();
    } else if (!this.props.goBack) {
      this.props.router!.goToParent();
    } else {
      this.props.goBack(response);
    }
  };

  public dynamicForm = (formRenderProps: FormRenderProps) => {
    const i18n = this.props?.i18n as I18n;
    return (
      <DynamicForm
        {...formRenderProps}
        Presentation={this.pickPresentationFormat()}
        fields={this.props.fields}
        values={formRenderProps.values}
        onChange={this.changed}
      >
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={formRenderProps.hasValidationErrors}
          className={this.props.classes.submitButton}
        >
          {i18n.t("commons:save", "Save")}
        </Button>
      </DynamicForm>
    );
  };

  private pickPresentationFormat(): React.FunctionComponent<
    PresentationComponentProps
  > {
    if (this.props.Presentation) {
      return this.props.Presentation;
    }

    if (!this.props.fields.fields[0].group) {
      return FlatForm;
    }

    return TabsForm;
  }

  private changed = (formState: FormState<Object>) => {
    this.removeErrorMessage(formState);
    this.props.dataSource.change(formState.values as T);
  };

  private removeErrorMessage(formState: FormState<Object>) {
    if (formState.active && formState.submitErrors) {
      formState.submitErrors[formState.active] = null;
    }
  }
}

export const CreateView = withStyles(viewStyles, { withTheme: true })(
  CreateViewComponent
);
